/* eslint-disable no-undef */
/* eslint-disable no-underscore-dangle */
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import TranslateIcon from "@mui/icons-material/Translate";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { get, post } from "../../services/Api/ApiFunctions";
import { GetUser, User } from "../../models/User";
import { PostProjectUser, Project, ProjectUser } from "../../models/Project";
import UsersManagementDialog from "../UsersManagementDialog/UsersManagementDialog";

type SelectUsersDialogProps = {
  open: boolean;
  onClose: (returnValue: string) => void;
};

function SelectUsersDialog({ open, onClose }: SelectUsersDialogProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [updated, setUpdated] = useState<boolean>(true);
  const [atLeastOneUserAdded, setAtLeastOneUser] = useState(false);
  const [openUsersManagementDialog, setOpenUsersManagementDialog] =
    useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchUsers = async (page: number) => {
    try {
      const allUsers: User[] = [];
      const fetchPage = async (currentPage: number) => {
        const res = await get<GetUser>(
          `/users?start=${10 * currentPage}&limit=10`
        );
        allUsers.push(...res.data.items);
        if (res.data.paginationInfo.hasNext) {
          await fetchPage(currentPage + 1);
        }
      };
      await fetchPage(page);
      setUsers(allUsers);
    } catch {
      toast.error(t("errors.generic-error"));
    }
  };

  const fetchProjectUsers = async () => {
    get<Project>(`/projects/${id}`)
      .then((res) => {
        setProjectUsers(res.data.users);
      })
      .catch(() => {
        toast.error(t("errors.generic-error"));
      });
  };

  useEffect(() => {
    if (open) {
      fetchUsers(0).then(() => {
        setFilteredUsers(users);
      });
      fetchProjectUsers();
    } else {
      setUsers([]);
      setFilteredUsers([]);
      setSearchQuery("");
    }
  }, [open, updated]);

  useEffect(() => {
    if (users.length > 0 && searchQuery === "") {
      setFilteredUsers(users);
    }
  }, [users]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        (user.lastName?.toLowerCase() || "").includes(query)
    );
    setFilteredUsers(filtered);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    user: User
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleAddUser = async (role: "tr" | "ad") => {
    if (!selectedUser) return;

    post<PostProjectUser>(`/projects/${id}/users`, {
      email: selectedUser.email,
      role,
    })
      .then(() => {
        toast.success(
          t(
            role === "ad"
              ? "users-management.add-admin-success"
              : "users-management.add-translator-success"
          )
        );
        setAtLeastOneUser(true);
        setUpdated((prev) => !prev);
      })
      .catch(() => {
        toast.error(t("users-management.add-user-error"));
      })
      .finally(() => {
        handleMenuClose();
      });
  };

  // Handles the close event of the dialog, if at least one user was added reload the parent page
  const handleDialogClose = () => {
    if (atLeastOneUserAdded) {
      setAtLeastOneUser(false);
      onClose("add");
    } else {
      onClose("closed");
    }
  };

  const handleUsersManagementDialogClose = (returnValue: string) => {
    setOpenUsersManagementDialog(false);

    if (returnValue === "created") setUpdated((prev) => !prev);
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <UsersManagementDialog
        open={openUsersManagementDialog}
        onClose={handleUsersManagementDialogClose}
        userId=""
        type="pl"
      />
      <DialogTitle>
        <Grid container>
          <Grid item xs={10}>
            <Typography
              data-cy="selectUsersManagementDialogTitle"
              sx={{ pt: 1 }}
            >
              {t("users-management.select-users-add")}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <IconButton
              data-cy="closeButton"
              sx={{ float: "right" }}
              onClick={handleDialogClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 0,
          height: "1000px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid
          container
          spacing={3}
          sx={{
            display: "flex",
            px: "24px",
            mb: 1,
            placeItems: "flex-end",
          }}
        >
          <Grid item xs={9}>
            <TextField
              fullWidth
              variant="standard"
              label={t("users-management.search")}
              InputLabelProps={{ style: { paddingLeft: "32px" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchQuery}
              onChange={handleSearch}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="contained"
              sx={{
                borderRadius: "25px",
                maxHeight: "36px",
              }}
              onClick={() => {
                setOpenUsersManagementDialog(true);
              }}
            >
              {t("settings.create-new-user")}
            </Button>
          </Grid>
        </Grid>
        <Box
          sx={{
            overflowX: isMobile ? "auto" : "visible",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <TableContainer>
            <Table
              aria-label="users table"
              sx={{
                tableLayout: isMobile ? "fixed" : "auto",
              }}
            >
              <TableHead>
                <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                  <TableCell>{t("users-management.email")}</TableCell>
                  <TableCell>{t("users-management.surname")}</TableCell>
                  <TableCell align="right" sx={{ pr: 4.5 }}>
                    {t("users-management.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <TableRow key={user._id}>
                      <TableCell
                        sx={{
                          wordWrap: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: isMobile ? "100%" : "150px",
                          minWidth: "100px",
                        }}
                      >
                        {user.email}
                      </TableCell>
                      <TableCell
                        sx={{
                          wordWrap: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: isMobile ? "100%" : "150px",
                          minWidth: "100px",
                        }}
                      >
                        {user.lastName}
                      </TableCell>
                      <TableCell align="right">
                        <Menu
                          anchorEl={anchorEl}
                          open={
                            Boolean(anchorEl) && selectedUser?._id === user._id
                          }
                          onClose={handleMenuClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          sx={{ mt: 0.5 }}
                        >
                          <MenuItem
                            onClick={() => handleAddUser("ad")}
                            disableRipple
                          >
                            <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                            {t("users-management.add-as-admin")}
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleAddUser("tr")}
                            disableRipple
                          >
                            <TranslateIcon sx={{ mr: 1 }} />
                            {t("users-management.add-as-translator")}
                          </MenuItem>
                        </Menu>
                        {projectUsers.some(
                          (projectUser) => projectUser.userId === user._id
                        ) ? (
                          <Button
                            variant="contained"
                            disabled
                            sx={{
                              borderRadius: "25px",
                              mr: 1,
                            }}
                          >
                            {t("users-management.already-added")}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            sx={{ borderRadius: "25px", mr: 1 }}
                            onClick={(event) => handleMenuClick(event, user)}
                          >
                            <MoreHorizIcon />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography>
                        {t("users-management.no-users-found")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default SelectUsersDialog;
